import {Modal, ModalContent, ModalFooter, ModalTitle, Button} from '@hm/fundamental'
import {mdiContentSave, mdiPencil} from '@mdi/js'
import {Stack, Box, useMediaQuery, Theme} from '@mui/material'
import React, {useCallback} from 'react'

import {HROC_CHECKLIST_FORM_WIDTH_IN_PX} from '../consts'
import {useSteppedFormContext, StepperLayoutType, useWidgetTranslation} from '../hooks'

import {HorizontalStepper} from './HorizontalStepper'
import {SideMenuStepper} from './SideMenuStepper'
import {StepForm, StepFormRef} from './StepForm'

type Props = {
  stepFormRef?: React.RefObject<StepFormRef>
}

type ExitConfirmationModalProps = {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  onDiscard: () => void
}

export const SteppedForm = ({stepFormRef}: Props) => {
  const {
    activeStep,
    stepsConfig,
    layout,
    readOnly,
    stepsValues,
    checklistConfig,
    stepActions,
    updateCurrentStepValues,
    showExitModal,
    handleCancelExit,
    handleConfirmExit,
    handleDiscardExit
  } = useSteppedFormContext()
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))
  const activeStepIndex = activeStep - 1
  const stepConfig = stepsConfig[activeStepIndex]
  const {StepFormContent, validationSchema, defaultValues, key} = stepConfig
  const stepValues = stepsValues[key] ?? defaultValues

  const handleStepClick = useCallback(
    (targetStep: number) => {
      if (readOnly) {
        stepActions.setStep(targetStep)
        return
      }

      if (stepFormRef?.current) {
        const currentFormValues = stepFormRef.current.getValues()
        updateCurrentStepValues(currentFormValues)
        stepActions.setStep(targetStep)
      }
    },
    [stepFormRef, updateCurrentStepValues, stepActions, readOnly]
  )

  const handleSaveAndConfirmExit = useCallback(() => {
    if (stepFormRef?.current && !readOnly) {
      const currentFormValues = stepFormRef.current.getValues()
      updateCurrentStepValues(currentFormValues)
    }
    handleConfirmExit(true) // Skip duplicate save since updateCurrentStepValues already saved
  }, [stepFormRef, readOnly, updateCurrentStepValues, handleConfirmExit])

  const stepForm = (
    <StepForm
      key={activeStep}
      ref={stepFormRef}
      validationSchema={validationSchema}
      defaultValues={defaultValues}
      values={stepValues}
      readOnly={readOnly}
    >
      <StepFormContent readOnly={readOnly} />
    </StepForm>
  )

  if (isMobile || layout === StepperLayoutType.Horizontal) {
    return (
      <>
        <HorizontalLayout
          stepper={<HorizontalStepper activeStep={activeStep} steps={stepsConfig} />}
          stepForm={stepForm}
        />
        <ExitConfirmationModal
          open={showExitModal}
          onClose={handleCancelExit}
          onConfirm={handleSaveAndConfirmExit}
          onDiscard={handleDiscardExit}
        />
      </>
    )
  }

  return (
    <>
      <SideMenuLayout
        stepper={
          <SideMenuStepper
            activeStep={activeStep}
            steps={stepsConfig}
            title={checklistConfig.title}
            onStepClick={handleStepClick}
          />
        }
        stepForm={stepForm}
      />
      <ExitConfirmationModal
        open={showExitModal}
        onClose={handleCancelExit}
        onConfirm={handleSaveAndConfirmExit}
        onDiscard={handleDiscardExit}
      />
    </>
  )
}

type LayoutProps = {
  stepper: React.ReactNode
  stepForm: React.ReactNode
}

const HorizontalLayout: React.FC<LayoutProps> = ({stepper, stepForm}) => {
  return (
    <Box width="100%" display="flex" justifyContent="center">
      <Stack spacing={3} alignItems="center" useFlexGap width="100%">
        <Box width={{xs: '100%', md: HROC_CHECKLIST_FORM_WIDTH_IN_PX}}>{stepper}</Box>
        {stepForm}
      </Stack>
    </Box>
  )
}

const SideMenuLayout: React.FC<LayoutProps> = ({stepper, stepForm}) => {
  return (
    <Box width="100%" display="flex" justifyContent="center">
      <Stack direction="row" spacing={2}>
        <Box
          sx={{
            width: {md: '240px', lg: '365px'}
          }}
        >
          {stepper}
        </Box>
        <Box sx={{width: HROC_CHECKLIST_FORM_WIDTH_IN_PX}}>{stepForm}</Box>
      </Stack>
    </Box>
  )
}

const ExitConfirmationModal: React.FC<ExitConfirmationModalProps> = ({
  open,
  onClose,
  onConfirm,
  onDiscard
}) => {
  const {t} = useWidgetTranslation()

  if (!open) return null

  return (
    <div data-test-id="exit-confirmation-modal">
      <Modal open={open} onClose={onClose}>
        <ModalTitle>{t('checklist.exitConfirmation.title')}</ModalTitle>
        <ModalContent>{t('checklist.exitConfirmation.message')}</ModalContent>
        <ModalFooter>
          <Button
            type="button"
            icon={mdiPencil}
            variant="secondary"
            size="small"
            onClick={onClose}
            data-test-id="exit-confirmation-continue-button"
          >
            {t('checklist.exitConfirmation.cancel')}
          </Button>
          <Button
            type="button"
            variant="tertiary"
            size="small"
            onClick={onDiscard}
            data-test-id="exit-confirmation-discard-button"
          >
            {t('checklist.exitConfirmation.discard')}
          </Button>
          <Button
            type="button"
            icon={mdiContentSave}
            size="small"
            onClick={onConfirm}
            data-test-id="exit-confirmation-save-button"
          >
            {t('checklist.exitConfirmation.confirm')}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}
